/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { getUserId } from "Models/User";
import { useAppAssetsWithSrc } from "Theme/EVENTS_ASSETS";
import { Button, Heading, Stack } from "components";
import useCompanion from "hooks/useCompanion";
import useRouting from "hooks/useRouting";
import useTryCatch from "hooks/useTryCatch";
import { updatePreferences } from "lib/preferencesApi";
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import ROUTES from "routes";
import useEventRouting from "hooks/useEventRouting";
import { useEnvs } from "state/EnvironmentsContext/EnvContext";
import { usePrefNavigator } from "state/PrefNavigatorContext";
import { useUI } from "state/UIContext";
import { useUser } from "state/UserContext";
import { ScreenPage } from "templates";
import { useTranslation } from "react-i18next";
import { pushToDataLayer } from "utils/gtm";

const ChoicePage = () => {
  const { t } = useTranslation();
  const { navigateWithEvent, eventId } = useEventRouting();
  const { userId } = useParams();
  const [companion, setCompanion] = useCompanion(userId, {
    stale: false,
  });
  const { colorScheme } = useUI();
  const { userData } = useUser();
  const EVENT_ASSETS = useAppAssetsWithSrc();

  const { getNextStepName, setCurrentStep } = usePrefNavigator();

  useEffect(() => {
    setCurrentStep(null);
  }, []);

  const onContinue = () => {
    const nextStepName = getNextStepName();
    if (nextStepName && ROUTES.choices[nextStepName]) {
      const routePath = ROUTES.choices[nextStepName](userId);
      navigateWithEvent(routePath);
      setCurrentStep(nextStepName);
    } else {
      return null;
    }
  };

  // const onContinue = () => {
  //   if (ROUTES.choices[currentStep]) {
  //     const routePath = ROUTES.choices[currentStep](userId);
  //     navigate(routePath);
  //   } else {
  //     return null;
  //   }
  // };

  const { shouldEnterDonePage } = useRouting();
  const handleOnSubmit = useTryCatch(async (e) => {
    e.preventDefault();
    const id = getUserId(companion) || userId;
    const api = await updatePreferences(
      id,
      {
        food_preferences: "",
        allergies: "",
        other_notes: "",
        isCompanion: id !== getUserId(userData),
      },
      eventId,
    );
    if (api.ok) {
      const newCompanionData = {
        ...companion,
        allergies: "",
        food_preferences: "",
        other_notes: "",
      };
      const newCompanions = setCompanion(newCompanionData);
      const goTo = await shouldEnterDonePage(newCompanions);

      navigateWithEvent(goTo);
    }
  });

  const { event: EVENT } = useEnvs();
  return (
    <ScreenPage
      desktopBG={
        colorScheme === "light"
          ? EVENT_ASSETS.choices_BG_desktop.src
          : EVENT_ASSETS.choices_BG_desktopDark.src
      }
      desktopBGFallback={
        colorScheme === "light"
          ? EVENT_ASSETS.choices_BG_desktop.fallbackSrc
          : EVENT_ASSETS.choices_BG_desktopDark.fallbackSrc
      }
      mobileBG={
        colorScheme === "light"
          ? EVENT_ASSETS.choices_BG_mobile.src
          : EVENT_ASSETS.choices_BG_mobileDark.src
      }
      mobileBGFallback={
        colorScheme === "light"
          ? EVENT_ASSETS.choices_BG_mobile.fallbackSrc
          : EVENT_ASSETS.choices_BG_mobileDark.fallbackSrc
      }
    >
      <Stack component="article">
        <Heading
          as="h2"
          className="text-xl md:text-2xl -text--theme-colors-text-title"
        >
          {t("choices.title")}
        </Heading>
        {EVENT !== "SPORTICO" && (
          <p className="-text--theme-colors-text-body text-sm md:text-base mt-4">
            {t("choices.description")}
          </p>
        )}
        <Stack className="mt-10 gap-y-3.5">
          <Stack className="gap-y-5">
            <Button
              size={"xl"}
              variant="secondary"
              onClick={(e) => {
                pushToDataLayer({
                  event: "skip_no_preferences_allergies",
                  screen_name: "preferences allergies intro",
                  journey_step: "preferences intro",
                });
                handleOnSubmit(e);
              }}
            >
              {t("choices.skipPreferenceOptionLabel")}
            </Button>
            <Button
              size={"xl"}
              variant="primary"
              onClick={() => {
                pushToDataLayer({
                  event: "preferences_intro_continue",
                  screen_name: "preferences allergies intro",
                  journey_step: "preferences intro",
                });
                onContinue();
              }}
            >
              {t("choices.continueButtonLabel")}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </ScreenPage>
  );
};
export default ChoicePage;
